import daphne from '@didi/daphne-sdk-javascript'
import { get } from 'lodash'
import { cached } from '@/utils/utils'
import { IS_PRE, IS_PRE_API, IS_SIM, IS_DEV } from './constants'
import * as appsDefault from './apps.default'
import * as appsDev from './apps.dev'
let appsDevWindow
try {
  appsDevWindow = JSON.parse(window._gattaran_config) // 从window获取配置，方便运行时调试
} catch (e) {}
/**
 * 获取配置逻辑
 * dev环境配置：src/config/apps.dev.js
 * 系统兜底配置：src/config/apps.default.js
 * sim环境配置：http://test-board.sailing.intra.xiaojukeji.com/#/configuration-center/organization/detail?configId=3312
 * pre环境配置：http://board.sailing.intra.xiaojukeji.com/#/configuration-center/organization/detail?configId=6502&scope=organization&serviceId=306
 * 线上环境配置：http://board.sailing.intra.xiaojukeji.com/#/configuration-center/organization/detail?configId=6262&scope=organization&serviceId=306
 */
// 预加载项目数量
let prefetchNum
// 接入子系统应用
let microApps
// 未接入子系统应用
let otherApps
// qiankun沙箱忽略拦截的js
let ignoreScript
// 子系统加载失败跳转到错误页面的错误类型
let jumpErrorPageCode
// 基座公共报错过滤规则
let commonErrorFilters
// 配置中心数据格式化
const formatApps = (apps, locations) => {
  if (!apps) return null
  return apps.map(name => {
    const app = parse(locations[name])
    if (Array.isArray(app)) {
      return {
        name,
        appLocation: parseLocation(app),
      }
    }
    return {
      ...app,
      name,
      appLocation: parseLocation(app.appLocation),
    }
  })
}
// json字符串转译
const parse = v => {
  try {
    return JSON.parse(v)
  } catch (e) {
    return null
  }
}
// 解析location中的正则
const parseLocation = (locations = []) => {
  return locations.map(location => {
    return location[0] === '^' ? new RegExp(location) : location
  })
}
const initConfig = ({
  microAppList,
  otherAppsList,
  prefetchNum,
  ignoreScript,
  jumpErrorPageCode,
  errorFilters,
  ...apps
}) => {
  try {
    prefetchNum = parse(prefetchNum)
    // eslint-disable-next-line no-unused-expressions
    prefetchNum === null ? console.error('初始化prefetchNum失败') : ''
    microApps = formatApps(parse(microAppList), apps)
    !microApps && console.error('初始化microApps失败')
    otherApps = formatApps(parse(otherAppsList), apps)
    !otherApps && console.error('初始化otherApps失败')
    ignoreScript = parse(ignoreScript)
    !ignoreScript && console.error('初始化ignoreScript失败')
    jumpErrorPageCode = parse(jumpErrorPageCode)
    !jumpErrorPageCode && console.error('初始化jumpErrorPageCode失败')
    commonErrorFilters = parse(errorFilters)
    !commonErrorFilters && console.error('初始化commonErrorFilters失败')
  } catch (e) {
    console.warn(e)
  }
}
// 是否使用dev环境apps配置
const useDevApps =
  process.env.VUE_APP_USE_DEV_APPS === 'ON' && process.env.NODE_ENV === 'development'
// 远端获取gtr-workshop节点配置参数
const defaultFetchConfig = {
  ns: 'sailing_m_services_gtr_workshop', // 命名空间
  global: true,
  key: 'user_key',
}
// 获取gtr-micro-apps-config配置参数
const getFetchConfig = () => {
  const config = {
    name: 'gtr-micro-apps-config', // 配置名称
    debug: false,
  }
  if (IS_DEV || IS_SIM) {
    config.debug = true
  }
  if (IS_PRE && IS_PRE_API) {
    config.name = 'gtr-micro-apps-config-pre'
  }
  return {
    ...defaultFetchConfig,
    ...config,
  }
}
const fetchMicroAppConfig = async () => {
  const config = getFetchConfig()
  const result = await daphne.getToggle(config)
  return get(result, `data.${config.name}.params`, {})
}
/**
 * MICRO_APPS 微应用配置
 * name: string; --- 子系统名跟项目中 package.json 中的name(名称)保持一致,规则 (b|c|d|i|gattaran) + (_|-) + 业务名称 + (_|-) + i18n
 * appLocation: (string|RegExp)[]; --- 项目的入口网关规则，支持正则
 * entry?: string; --- 项目的入口配置（不带 origin） 该字段默认取`appLocation`的第一位，仅支持字符串
 * devEntry?: string; dev环境的子系统入口,默认为http://localhost:8080
 */
export const getMicroApps = () => {
  if (appsDevWindow?.microApps) return appsDevWindow.microApps
  if (useDevApps) {
    return appsDev.microApps
  }
  return microApps || appsDefault.microApps
}
export const getOtherApps = () => {
  if (appsDevWindow?.otherApps) return appsDevWindow.otherApps
  if (useDevApps) {
    return appsDev.otherApps
  }
  return otherApps || appsDefault.otherApps
}
export const getPrefetchNum = () => {
  if (appsDevWindow?.prefetchNum) return appsDevWindow.prefetchNum
  if (useDevApps) {
    return appsDev.prefetchNum
  }
  return prefetchNum || appsDefault.prefetchNum
}
export const getIgnoreScript = () => {
  if (appsDevWindow?.ignoreScript) return appsDevWindow.ignoreScript
  if (useDevApps) {
    return appsDev.ignoreScript
  }
  return ignoreScript || appsDefault.ignoreScript
}
export const getJumpErrorPageCode = () => {
  if (appsDevWindow?.jumpErrorPageCode) return appsDevWindow.jumpErrorPageCode
  if (useDevApps) {
    return appsDev.jumpErrorPageCode
  }
  return jumpErrorPageCode || appsDefault.jumpErrorPageCode
}

// 返回公共报错设置
export const getCommonErrorFilters = () => {
  return commonErrorFilters || []
}

// 初始化子系统配置
export const initMicroConfig = async () => {
  try {
    const config = await fetchMicroAppConfig()
    initConfig(config)
  } catch (e) {
    console.error('初始化子系统基础配置失败')
    console.error(e)
  }
}
// 返回路由匹配函数
export const matchMicroAppLocation = path => {
  return location => {
    if (typeof location === 'string') {
      const check = new RegExp(`^${location}`).test(path)
      if (!check) return false
      // bugfix 字符串时路径需匹配到末尾的/
      const len = location.length
      if (path.length > len) {
        return path.charAt(len) === '/'
      }
      return true
    } else {
      return location.test(path)
    }
  }
}
// 匹配未接入子系统路由
export const matchNoRebuildAppRoute = cached(function (path) {
  const matchLocation = matchMicroAppLocation(path)
  return (
    getOtherApps()
      .reduce((prev, curr) => {
        prev = prev.concat(curr.appLocation)
        return prev
      }, [])
      .filter(matchLocation).length > 0
  )
})
